import React, { useState } from 'react';
import { Upload, Button } from 'antd';
import { useDispatch } from 'react-redux';
import { msg } from 'src/tools/message';
import Modal from '@/src/components/Antd/Modal/Modal';
import { reselectProducts } from '../slice';
import { parseExcelFile, terminateCurrentWorker } from './ExcelParser';
import { validateStockItems, ValidateResult } from './DataValidator';
import { transformToProductItems } from './DataTransformer';
import css from './BulkStockImport.less';
import { Icon } from '@/src/tools/icon';
import { ImportStockItem } from './ExcelParser';
import { useI18n } from '@/src/hooks/useRouterChange';
import {
  getI18nFromGeneral,
  i18nGet,
  replaceI18nVariables,
} from '@/src/tools/tools';

interface BulkStockImportProps {
  visible: boolean;
  onCancel: () => void;
  currentProducts: any[];
}

enum ImportStep {
  Upload = 'upload',
  Uploading = 'uploading',
  ValidatingAPI = 'validating_api',
  ValidationResult = 'validation_result',
  Error = 'error',
}

const { Dragger } = Upload;

// 自定义UploadFile接口以避免antd导入错误
interface UploadFile {
  uid: string;
  name: string;
  size: number;
  type: string;
  status: 'uploading' | 'done' | 'error';
}

const BulkStockImport: React.FC<BulkStockImportProps> = ({
  visible,
  onCancel,
  currentProducts,
}) => {
  const dispatch = useDispatch();

  const [step, setStep] = useState<ImportStep>(ImportStep.Upload);
  const [file, setFile] = useState<File | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [validationResult, setValidationResult] =
    useState<ValidateResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [parsedItems, setParsedItems] = useState<ImportStockItem[]>([]);
  const [progress, setProgress] = useState(0);
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);

  // 清理状态并返回上传步骤
  const resetToUpload = () => {
    setFile(null);
    setFileList([]);
    setValidationResult(null);
    setErrorMessage(null);
    setParsedItems([]);
    setProgress(0);
    setStep(ImportStep.Upload);
  };

  // 调用onCancel并手动关闭worker
  const onCancelAndStopWorker = () => {
    terminateCurrentWorker();
    if (onCancel) onCancel();
  };

  // 处理文件上传
  const handleFileUpload = async (file: File) => {
    // 简单的文件类型前端校验
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    if (!allowedTypes.includes(file.type)) {
      setErrorMessage(getI18n('stockIn.stock.file_formattion_error'));
      setStep(ImportStep.Error);
      setFile(file); // 仍然设置文件以便在错误消息中显示名称
      return;
    }

    // 创建兼容 antd Upload 的文件对象
    const uploadFile: UploadFile = {
      uid: Date.now().toString(),
      name: file.name,
      size: file.size,
      type: file.type,
      status: 'uploading',
    };

    setFileList([uploadFile]);
    setFile(file);
    setStep(ImportStep.Uploading);
    setProgress(0); // 重置进度
    setErrorMessage(null); // 清除之前的错误信息

    // 模拟上传进度
    const timer = setInterval(() => {
      setProgress((prev) => {
        const nextProgress = prev + Math.floor(Math.random() * 10) + 5; // 更随机的进度
        if (nextProgress >= 100) {
          clearInterval(timer);
          return 100;
        }
        return nextProgress;
      });
    }, 150);

    // 文件读取和解析完成后触发
    try {
      // 直接调用 processExcelFile，不再依赖 setTimeout
      await processExcelFile(file, uploadFile);
      clearInterval(timer); // 解析完成，清除模拟进度条
      setProgress(100);
      // 状态在 processExcelFile 内部设置
    } catch (error) {
      clearInterval(timer);
      // 错误状态已在 processExcelFile 或其调用的函数中设置
    }
  };

  // 处理 Excel 文件
  const processExcelFile = async (file: File, uploadFile: UploadFile) => {
    try {
      // 解析Excel文件
      const result = await parseExcelFile(file, i18n);

      // 解析出错
      if (result.hasError) {
        setErrorMessage(result.errorMessage || 'Failed to parse file');
        setStep(ImportStep.Error);
        return; // 停止后续流程
      }

      // 解析成功，更新列表状态并进入API验证阶段
      setParsedItems(result.items);
      setFileList([
        {
          ...uploadFile,
          status: 'done',
        },
      ]);
      setStep(ImportStep.ValidatingAPI); // 进入API验证加载状态

      // 校验SKU (API Call)
      const validationApiResponse = await validateStockItems(
        result.items,
        i18n,
      );

      // API调用出错
      if (!validationApiResponse.success) {
        setErrorMessage(
          validationApiResponse.errorMessage || 'Failed to validate SKUs',
        );
        setStep(ImportStep.Error);
        return; // 停止后续流程
      }

      if (validationApiResponse.result.validSkus.length === 0) {
        setErrorMessage(getI18n('stockIn.stock.judge_file_content_failed'));
        setStep(ImportStep.Error);
        return; // 停止后续流程
      }

      // API调用成功
      setValidationResult(validationApiResponse.result); // 确保 validationApiResponse.result 不是 undefined
      setStep(ImportStep.ValidationResult);
    } catch (error) {
      console.error('Error processing Excel file or validating SKUs:', error);
      setErrorMessage('An unexpected error occurred during file processing.');
      setStep(ImportStep.Error);
    }
  };

  // 处理导入确认
  const handleImport = () => {
    // 导入按钮在验证成功时才可点击，此处无需额外判断 step
    if (!validationResult || !parsedItems) return;

    try {
      // 创建产品映射
      const parsedItemsMap = {};
      const validSkus = validationResult.validSkus;
      parsedItems.forEach((item) => (parsedItemsMap[item.sku] = item));

      // 转换为产品项
      const productItems = transformToProductItems(validSkus, parsedItemsMap); // 第二个参数可能需要实际的产品信息

      // 使用已有的 reselectProducts action 更新产品列表 (替换或合并)
      // 这里假设是替换当前选择
      dispatch(
        reselectProducts({
          products: productItems, // 只导入本次上传验证通过的产品
        }),
      );

      // 显示成功消息
      msg.success(getI18n('stockIn.stock.products_imported_successfully'));

      // 关闭弹窗
      onCancel();
    } catch (error) {
      console.error('Error during import:', error);
      setErrorMessage('Failed to import products. Please try again.');
      setStep(ImportStep.Error);
    }
  };

  // 上传控件配置
  const uploadProps = {
    name: 'file',
    multiple: false,
    accept: '.xlsx,.xls', // 明确支持的文件类型
    beforeUpload: (file: File) => {
      handleFileUpload(file);
      return false; // 阻止 antd 的默认上传行为
    },
    fileList: fileList, // 控制文件列表展示
    showUploadList: false, // 不显示 antd 的默认文件列表
    disabled:
      step === ImportStep.Uploading || step === ImportStep.ValidatingAPI, // 上传和API验证过程中禁用
  };

  // 渲染验证结果
  const renderValidationResult = () => {
    if (!validationResult) return null;

    const { validCount, invalidSkus } = validationResult;
    const totalCount = parsedItems.length; // 使用解析出的条目总数

    return (
      <div className={css.validationResultContainer}>
        <div className={css.uploadHeader}>
          {renderUploadFileTitle()}
          {renderTemplateDownload()}
        </div>
        <div className={css.fileInfo}>
          <div className={css.fileName}>
            <Icon type='iconfile' />{' '}
            {file?.name || getI18n('stockIn.stock.unknown_file')}
          </div>
        </div>
        {invalidSkus.length > 0 ? (
          <div className={css.resultWithErrors}>
            <div className={css.resultStatusText} style={{ color: '#52c41a' }}>
              {replaceI18nVariables(
                getI18n('stockIn.stock.sku_validate_result'),
                validCount,
                totalCount,
              )}
            </div>
            <div className={css.invalidSkuSection}>
              <div className={css.invalidSkuTitle}>
                {getI18n('stockIn.stock.map_no_product')}
              </div>
              <div className={css.skuList}>
                <div className={css.skuTItle}>SKU</div>
                <div className={css.skuItemsPanel}>
                  {invalidSkus.join(', ')}
                </div>
              </div>
              <div className={css.importNote}>
                {getI18n('stockIn.stock.importing_tips1')}
                <br></br>
                {getI18n('stockIn.stock.importing_tips2')}
                <br></br>
                {getI18n('stockIn.stock.importing_tips3')}
                <br></br>
                {getI18n('stockIn.stock.importing_tips4')}
              </div>
            </div>
          </div>
        ) : (
          <div className={css.resultSuccess}>
            <div className={css.resultStatusText} style={{ color: '#52c41a' }}>
              {replaceI18nVariables(
                getI18n('stockIn.stock.sku_validate_result'),
                validCount,
                totalCount,
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // 渲染错误信息
  const renderError = () => {
    if (!errorMessage) return null;

    return (
      <div className={css.errorDisplayContainer}>
        <div className={css.uploadHeader}>
          {renderUploadFileTitle()}
          {renderTemplateDownload()}
        </div>
        <div className={css.fileInfo}>
          {/* 保留文件名显示 */}
          <div className={css.fileName}>
            <Icon type='iconfile' />{' '}
            {file?.name || getI18n('stockIn.stock.unknown_file')}
          </div>
        </div>
        {/* 错误文本 */}
        <div className={css.errorMessageText}>{errorMessage}</div>
      </div>
    );
  };

  // 渲染上传中状态
  const renderUploading = () => {
    return (
      <div className={css.uploadingContainer}>
        <div className={css.uploadHeader}>
          {renderUploadFileTitle()}
          {renderTemplateDownload()}
        </div>
        <div className={css.fileInfo}>
          <div className={css.fileName}>
            <Icon type='iconfile' />{' '}
            {file?.name || getI18n('stockIn.stock.unknown_file')}
          </div>
        </div>
        <div className={css.progressBarContainer}>
          <div
            className={css.progressBar}
            style={{ width: `${progress}%`, backgroundColor: '#52c41a' }}
          />
        </div>
        {renderInstructionText()}
      </div>
    );
  };

  // 渲染API验证加载状态
  const renderValidatingAPI = () => {
    return (
      <div className={css.validatingApiContainer}>
        <div className={css.uploadHeader}>
          {renderUploadFileTitle()}
          {renderTemplateDownload()}
        </div>
        <div className={css.fileInfo}>
          <div className={css.fileName}>
            <Icon type='iconfile' />{' '}
            {file?.name || getI18n('stockIn.stock.unknown_file')}
          </div>
        </div>
        {/* Skeleton Loader */}
        <div className={css.skeletonLoader}>
          <div className={css.skeletonLine} style={{ width: '90%' }}></div>
          <div className={css.skeletonLine} style={{ width: '100%' }}></div>
          <div className={css.skeletonLine} style={{ width: '95%' }}></div>
          <div className={css.skeletonLine} style={{ width: '98%' }}></div>
          <div className={css.skeletonLine} style={{ width: '92%' }}></div>
        </div>
      </div>
    );
  };

  // 渲染模板下载链接
  const renderTemplateDownload = () => {
    return (
      <span>
        {getI18n('general.download')}{' '}
        <a
          className={css.downloadLink}
          href='https://resource2.unifyxp.com/portal/Stock+in+template.xlsx'
          download
          style={{ color: '#52c41a' }}>
          {getI18n('stockIn.stock.stock_in_file')}
        </a>
      </span>
    );
  };

  const renderInstructionText = () => {
    return (
      <div className={css.instructionText}>
        <div>{getI18n('stockIn.stock.instruction')}</div>
        <div>{getI18n('stockIn.stock.bulk_stock_in_step1')}</div>
        <div>{getI18n('stockIn.stock.bulk_stock_in_step2')}</div>
        <div>{getI18n('stockIn.stock.bulk_stock_in_step3')}</div>
        <div>{getI18n('stockIn.stock.overwrite_notice')}</div>
      </div>
    );
  };

  // 渲染导入文件进度小标题
  const renderUploadFileTitle = () => {
    const titleTextMap = {
      [ImportStep.Upload]: getI18n('stockIn.stock.upload_file'),
      [ImportStep.Uploading]: getI18n('stockIn.stock.uploading_file'),
      [ImportStep.ValidatingAPI]: getI18n('stockIn.stock.uploaded_file'),
      [ImportStep.ValidationResult]: getI18n('stockIn.stock.uploaded_file'),
      [ImportStep.Error]: getI18n('stockIn.stock.uploaded_file'),
    };

    return <div className={css.uploadTitle}>{titleTextMap[step]}</div>;
  };

  // 渲染上传区域
  const renderUploadArea = () => {
    return (
      <div>
        <div className={css.uploadHeader}>
          {renderUploadFileTitle()}
          {renderTemplateDownload()}
        </div>
        <div className={css.uploadSection}>
          <Dragger {...uploadProps} className={css.dragger}>
            <div className={css.draggerContent}>
              <div className={css.dropText}>
                {getI18nFromGeneral('general.antd.Uploader.tip')}
              </div>
              <div className={css.orText}>
                {getI18nFromGeneral('general.antd.Uploader.or')}
              </div>
              <Button className={css.uploadButton}>
                {getI18n('stockIn.stock.upload_file')}
              </Button>
            </div>
          </Dragger>
        </div>
        {renderInstructionText()}
      </div>
    );
  };

  // 根据当前步骤渲染内容
  const renderContent = () => {
    switch (step) {
      case ImportStep.Upload:
        return renderUploadArea();
      case ImportStep.Uploading:
        return renderUploading();
      case ImportStep.ValidatingAPI:
        return renderValidatingAPI();
      case ImportStep.ValidationResult:
        return renderValidationResult();
      case ImportStep.Error:
        return renderError(); // 错误状态单独渲染，不含上传区
      default:
        return null;
    }
  };

  // 根据当前步骤获取标题
  const getModalTitle = () => {
    return getI18n('stockIn.stock.import_stock_list');
  };

  // 根据当前步骤渲染底部按钮
  const renderFooter = () => {
    const isValidationResult = step === ImportStep.ValidationResult;
    // const isValidatingAPI = step === ImportStep.ValidatingAPI;
    // const isUploading = step === ImportStep.Uploading;

    // Import 按钮的禁用逻辑: 只有验证成功时才启用
    const importDisabled = !isValidationResult;

    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={onCancelAndStopWorker} style={{ marginRight: 8 }}>
          {getI18n('general.cancel')}
        </Button>
        <Button
          type='primary'
          onClick={handleImport}
          style={{
            // 按钮激活时的样式
            backgroundColor: isValidationResult ? '#52c41a' : undefined,
            borderColor: isValidationResult ? '#52c41a' : undefined,
          }}
          disabled={importDisabled}>
          {getI18n('general.import')}
        </Button>
      </div>
    );
  };

  return (
    <Modal
      visible={visible}
      title={getModalTitle()}
      onCancel={onCancelAndStopWorker}
      className={css.bulkImportModal}
      footer={renderFooter()}
      afterClose={resetToUpload}>
      <div className={css.container}>{renderContent()}</div>
    </Modal>
  );
};

export default BulkStockImport;
